import React, { useMemo, useCallback } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import { Box, xcss } from '@atlaskit/primitives';
import Tooltip from '@atlaskit/tooltip';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { ThemedButtonWrapper } from '@atlassian/jira-project-theme-components/src/ui/themed-button-new/ThemedButtonNew.tsx';
import { getIsSoftwareThemingEnabled } from '@atlassian/jira-project-theme-providers/src/utils/getIsSoftwareThemingEnabled.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { CMPCustomFilter, Filters } from '../../../common/types.tsx';
import HiddenCheckbox from './hidden-checkbox/index.tsx';
import Label from './label/index.tsx';

type Props = {
	onChange: (filters: Filters, analyticsEvent: UIAnalyticsEvent) => void;
	selectedFilters: Set<string>;
	filter: CMPCustomFilter;
	isThemed?: boolean;
};

const FilterOption = (props: Props) => {
	const { onChange, selectedFilters, filter, isThemed } = props;
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const isSelected = useMemo(() => selectedFilters.has(filter.id), [selectedFilters, filter]);

	const onClick = useCallback(() => {
		if (isSelected) {
			selectedFilters.delete(filter.id);
		} else {
			selectedFilters.add(filter.id);
		}

		onChange({ CUSTOM_FILTER: Array.from(selectedFilters) }, createAnalyticsEvent({}));
	}, [onChange, filter, isSelected, selectedFilters, createAnalyticsEvent]);

	const id = fg('jsw-cmp-quick-filter-same-name-issue')
		? `checkbox-id-${filter.name}-${filter.id}`
		: `checkbox-id-${filter.name}`;

	return (
		<Tooltip
			content={filter.description}
			position="bottom"
			data-testid="software-filters.ui.filter-selection-bar.filter-option.tooltip"
		>
			<Box xcss={maxWidthWrapperStyles}>
				<HiddenCheckbox id={id} checked={isSelected} onChange={onClick} />
				{isThemed && getIsSoftwareThemingEnabled() ? (
					<ThemedButtonWrapper>
						<Button id={id} isSelected={isSelected} component={Label}>
							{filter.name}
						</Button>
					</ThemedButtonWrapper>
				) : (
					<Button id={id} isSelected={isSelected} component={Label}>
						{filter.name}
					</Button>
				)}
			</Box>
		</Tooltip>
	);
};

export default FilterOption;

const maxWidthWrapperStyles = xcss({
	maxWidth: '380px',
});
